import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { createPayPalOrder, capturePayPalOrder } from '../lib/paypal';
import { supabase } from '../lib/supabase';
import TransactionBreakdown from './TransactionBreakdown';
import type { RegistryItem, RSVPResponse } from '../types/registry';

interface Props {
  item: RegistryItem;
  onClose: () => void;
  onClaim: () => void;
  rsvpData?: RSVPResponse;
}

export default function GuestClaimModal({ item, onClose, onClaim, rsvpData }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPayPal, setShowPayPal] = useState(false);
  const [loadingRegistryDetails, setLoadingRegistryDetails] = useState(true);
  const [guestUserId, setGuestUserId] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: ''
  });
  const [registryDetails, setRegistryDetails] = useState<{
    fee_responsibility: 'COUPLE' | 'GUEST' | 'SPLIT';
    platform_fee_percentage: number;
    gateway_fee_percentage: number;
    user_id: string;
  } | null>(null);

  useEffect(() => {
    loadRegistryDetails();
  }, []);

  useEffect(() => {
    if (rsvpData) {
      const nameParts = rsvpData.name.split(' ');
      setUserInfo(prev => ({
        ...prev,
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: rsvpData.email || '',
        phone: rsvpData.phone || ''
      }));
    }
  }, [rsvpData]);

  const loadRegistryDetails = async () => {
    try {
      setLoadingRegistryDetails(true);
      const { data, error } = await supabase
        .from('registries')
        .select('fee_responsibility, user_id')
        .eq('id', item.registry_id)
        .single();

      if (error) throw error;
      if (data) {
        const { data: platformFeeData } = await supabase
          .from('system_settings')
          .select('value')
          .eq('key', 'platform_fee_rate')
          .maybeSingle();

        const { data: gatewayFeeData } = await supabase
          .from('system_settings')
          .select('value')
          .eq('key', 'gateway_fee_rate')
          .maybeSingle();

        const currentPlatformFee = platformFeeData ? parseFloat(platformFeeData.value) : 0;
        const currentGatewayFee = gatewayFeeData ? parseFloat(gatewayFeeData.value) : 0;

        setRegistryDetails({
          fee_responsibility: data.fee_responsibility,
          platform_fee_percentage: currentPlatformFee,
          gateway_fee_percentage: currentGatewayFee,
          user_id: data.user_id
        });
      }
    } catch (err) {
      console.error('Error loading registry details:', err);
    } finally {
      setLoadingRegistryDetails(false);
    }
  };

  const handleShowPayPal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userInfo.firstName || !userInfo.lastName || !userInfo.email || !userInfo.phone || !userInfo.address || !userInfo.city) {
      setError('Please fill in all required fields');
      return;
    }
    setError('');

    try {
      setLoading(true);
      const { data: guestUser, error: guestError } = await supabase
        .from('guest_users')
        .insert([{
          name: `${userInfo.firstName} ${userInfo.lastName}`.trim(),
          email: userInfo.email
        }])
        .select()
        .single();

      if (guestError) throw guestError;
      if (!guestUser) throw new Error('Failed to create guest user');

      setGuestUserId(guestUser.id);
      setShowPayPal(true);
    } catch (err: any) {
      setError(err.message || 'Failed to proceed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/75 z-50 flex flex-col justify-end sm:items-center sm:justify-center sm:p-4">
      {/* Backdrop tap-to-close */}
      <div className="absolute inset-0" onClick={onClose} />

      <div className="relative w-full sm:max-w-2xl lg:max-w-4xl bg-gray-800 sm:bg-gray-800/95 sm:backdrop-blur-sm rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[92vh] sm:max-h-[90vh]">

        {/* Drag handle — mobile only */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-gray-600" />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-4 pb-4 sm:px-6 sm:pt-5 border-b border-gray-700 flex-shrink-0">
          <div className="flex-1 min-w-0 pr-3">
            <h3 className="text-lg sm:text-xl font-semibold text-white leading-snug">Claim Gift</h3>
            <p className="mt-1 text-sm text-gray-400 truncate">Complete your info to claim {item.name}</p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-2 rounded-full text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">
          {error && (
            <div className="mb-5 bg-red-900/50 text-red-200 p-4 rounded-lg text-sm">
              {error}
            </div>
          )}

          {loadingRegistryDetails ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
              <span className="ml-3 text-gray-300 text-sm">Loading payment information...</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-5">
                {/* Item summary */}
                <div className="bg-gray-900/60 p-4 rounded-xl">
                  <div className="flex justify-between items-center gap-4">
                    <div className="min-w-0">
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Item</p>
                      <p className="text-base font-semibold text-white truncate">{item.name}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Price</p>
                      <p className="text-base font-semibold text-white">LKR {item.price.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {!showPayPal ? (
                  <form onSubmit={handleShowPayPal} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1.5">First Name *</label>
                        <input
                          type="text"
                          value={userInfo.firstName}
                          onChange={(e) => setUserInfo({ ...userInfo, firstName: e.target.value })}
                          className="w-full px-3 py-2.5 bg-gray-900/60 border border-gray-700 text-white rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-500"
                          placeholder="First name"
                          required
                          disabled={loading}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1.5">Last Name *</label>
                        <input
                          type="text"
                          value={userInfo.lastName}
                          onChange={(e) => setUserInfo({ ...userInfo, lastName: e.target.value })}
                          className="w-full px-3 py-2.5 bg-gray-900/60 border border-gray-700 text-white rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-500"
                          placeholder="Last name"
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1.5">Email *</label>
                      <input
                        type="email"
                        value={userInfo.email}
                        onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
                        className="w-full px-3 py-2.5 bg-gray-900/60 border border-gray-700 text-white rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-500"
                        placeholder="Enter your email address"
                        required
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1.5">Phone *</label>
                      <input
                        type="tel"
                        value={userInfo.phone}
                        onChange={(e) => setUserInfo({ ...userInfo, phone: e.target.value })}
                        className="w-full px-3 py-2.5 bg-gray-900/60 border border-gray-700 text-white rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-500"
                        placeholder="Enter your phone number"
                        required
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1.5">Address *</label>
                      <input
                        type="text"
                        value={userInfo.address}
                        onChange={(e) => setUserInfo({ ...userInfo, address: e.target.value })}
                        className="w-full px-3 py-2.5 bg-gray-900/60 border border-gray-700 text-white rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-500"
                        placeholder="Enter your address"
                        required
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1.5">City *</label>
                      <input
                        type="text"
                        value={userInfo.city}
                        onChange={(e) => setUserInfo({ ...userInfo, city: e.target.value })}
                        className="w-full px-3 py-2.5 bg-gray-900/60 border border-gray-700 text-white rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-500"
                        placeholder="Enter your city"
                        required
                        disabled={loading}
                      />
                    </div>

                    {/* Transaction breakdown — shown inline on mobile, in sidebar on desktop */}
                    {registryDetails && (
                      <div className="lg:hidden space-y-3 pt-1">
                        <TransactionBreakdown
                          originalAmount={item.price}
                          currency="LKR"
                          showDetailed={false}
                          feeResponsibility={registryDetails.fee_responsibility}
                          platformFeePercentage={registryDetails.platform_fee_percentage}
                          gatewayFeePercentage={registryDetails.gateway_fee_percentage}
                        />
                      </div>
                    )}

                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className="flex-1 px-4 py-3 text-sm text-gray-300 hover:text-white bg-gray-700/50 rounded-lg hover:bg-gray-600/50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 px-4 py-3 text-sm bg-primary text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition-colors font-medium"
                      >
                        {loading ? 'Processing...' : 'Proceed to Payment'}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-gray-900/60 p-4 rounded-xl">
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Paying as</p>
                      <p className="text-white font-medium text-sm">{userInfo.firstName} {userInfo.lastName}</p>
                      <p className="text-gray-400 text-sm">{userInfo.email}</p>
                    </div>

                    {/* Transaction breakdown — shown inline on mobile, in sidebar on desktop */}
                    {registryDetails && (
                      <div className="lg:hidden">
                        <TransactionBreakdown
                          originalAmount={item.price}
                          currency="LKR"
                          showDetailed={false}
                          feeResponsibility={registryDetails.fee_responsibility}
                          platformFeePercentage={registryDetails.platform_fee_percentage}
                          gatewayFeePercentage={registryDetails.gateway_fee_percentage}
                        />
                      </div>
                    )}

                    <div className="bg-white rounded-xl p-4">
                      <PayPalScriptProvider options={{ clientId: import.meta.env.VITE_PAYPAL_CLIENT_ID, currency: 'USD' }}>
                        <PayPalButtons
                          style={{ layout: 'vertical', color: 'blue', shape: 'rect', label: 'pay' }}
                          createOrder={async () => {
                            setError('');
                            const response = await createPayPalOrder({
                              itemId: item.id,
                              itemName: item.name,
                              originalAmount: item.price,
                              currency: 'LKR',
                              feeResponsibility: registryDetails?.fee_responsibility || 'GUEST',
                              platformFeePercentage: registryDetails?.platform_fee_percentage || 0,
                              gatewayFeePercentage: registryDetails?.gateway_fee_percentage || 0,
                              registryId: item.registry_id,
                              registryOwnerId: registryDetails?.user_id || '',
                              payerFirstName: userInfo.firstName,
                              payerLastName: userInfo.lastName,
                              payerEmail: userInfo.email,
                              guestId: guestUserId || undefined,
                            });
                            return response.orderId;
                          }}
                          onApprove={async (data) => {
                            try {
                              await capturePayPalOrder(data.orderID);
                              onClaim();
                            } catch (err: any) {
                              setError(err.message || 'Payment capture failed');
                            }
                          }}
                          onError={(err) => {
                            console.error('PayPal error:', err);
                            setError('Payment failed. Please try again.');
                          }}
                          onCancel={() => {
                            setLoading(false);
                          }}
                        />
                      </PayPalScriptProvider>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPayPal(false)}
                      className="w-full px-4 py-3 text-sm text-gray-300 hover:text-white bg-gray-700/50 rounded-lg hover:bg-gray-600/50 transition-colors"
                    >
                      Back to Edit Details
                    </button>
                  </div>
                )}
              </div>

              {/* Sidebar — desktop only */}
              {registryDetails && (
                <div className="hidden lg:block space-y-4">
                  <TransactionBreakdown
                    originalAmount={item.price}
                    currency="LKR"
                    showDetailed={false}
                    feeResponsibility={registryDetails.fee_responsibility}
                    platformFeePercentage={registryDetails.platform_fee_percentage}
                    gatewayFeePercentage={registryDetails.gateway_fee_percentage}
                  />
                  <div className="bg-gray-900/40 p-3 rounded-lg">
                    <p className="text-xs text-gray-400 text-center">
                      Your payment is secure and processed by PayPal
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
